import React, { useState } from 'react';
import {
  Rocket, Check, Loader2, ExternalLink, Copy, History, Radio,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNowStrict } from 'date-fns';
import { es as esLocale, enUS as enLocale } from 'date-fns/locale';
import { usePublishStatus } from '../hooks/usePublishStatus';
import { useLocale } from '../contexts/LocaleContext';

interface Props {
  workspaceId: string;
  onShowHistory?: () => void;
  /** Compact single-line mode for the header (default: false = spacious). */
  compact?: boolean;
}

/**
 * PublishStatusBar — one-tap deploy CTA (v14).
 *
 * v14 removed the intermediate confirm dialog. Tapping Publicar
 * triggers the full pipeline immediately:
 *   1) POST /publish → backend builds, resolves target automatically
 *      (cloudflare_pages for static/SPA, fly_io for full-stack),
 *      deploys, records the version. Falls back to nexus_hosted when
 *      an external target's credentials misbehave.
 *   2) usePublishStatus polls the readiness/history so the pill flips
 *      from "Publicar" to "🟢 En vivo v1" as soon as the deploy lands.
 *   3) A toast surfaces the public URL with a one-tap copy affordance.
 *
 * The status polls every 10 s so the pill always reflects the
 * canonical state on the server, even when someone else in the same
 * workspace triggered the publish.
 */
export const PublishStatusBar: React.FC<Props> = ({
  workspaceId, onShowHistory, compact = false,
}) => {
  const status = usePublishStatus(workspaceId);
  const { t, locale } = useLocale();
  const [copied, setCopied] = useState(false);

  const runPublish = async () => {
    // Fire the entire pipeline. Backend picks the target based on
    // project_kind and falls back gracefully — no manual choice.
    toast.loading(t('publish.inFlight'), { id: 'publish-progress' });
    const res = await status.publish('auto');
    toast.dismiss('publish-progress');
    if (!res) {
      toast.error(t('publish.failed'));
      return;
    }
    // Pick the toast copy that matches where the deploy actually landed
    let msg: string;
    if (res.fallbackReason && res.target === 'nexus_hosted') {
      const requestedHost = 'Cloudflare/Fly';
      msg = t('publish.fallback', { host: requestedHost, version: res.version });
    } else if (res.target === 'cloudflare_pages') {
      msg = t('publish.successCloudflare', { version: res.version });
    } else if (res.target === 'fly_io') {
      msg = t('publish.successFly', { version: res.version });
    } else if (res.target === 'nexus_hosted') {
      msg = t('publish.successNexus', { version: res.version });
    } else {
      msg = t('publish.successGeneric', { version: res.version });
    }
    toast.success(msg, {
      duration: 8000,
      description: res.url,
      action: {
        label: t('workspace.copyUrl'),
        onClick: () => {
          navigator.clipboard.writeText(res.url).catch(() => {});
          toast.success(t('publish.urlCopied'));
        },
      },
    });
  };

  const copyPublicUrl = () => {
    if (!status.publicUrl) return;
    navigator.clipboard.writeText(status.publicUrl).then(() => {
      setCopied(true);
      toast.success(t('publish.urlCopied'));
      setTimeout(() => setCopied(false), 1400);
    });
  };

  // ─── Not yet published ──────────────────────────────────────────
  if (!status.activeVersion) {
    const notReadyHint = status.checks
      .filter((c) => !c.ok)
      .map((c) => c.hint || c.name)
      .join(', ');
    return (
      <button
        type="button"
        onClick={runPublish}
        disabled={!status.ready || status.publishing}
        data-testid="publish-btn"
        title={status.ready
          ? `${t('publish.button')} (${status.projectKind})`
          : t('publish.notReady', { reasons: notReadyHint || '...' })}
        className="hidden md:inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          background: status.ready ? 'hsl(var(--accent))' : 'hsl(var(--bg-elev) / 0.6)',
          border: `1px solid hsl(var(--${status.ready ? 'accent' : 'border'}) / ${status.ready ? 1 : 0.6})`,
          color: status.ready ? '#0a0f10' : 'hsl(var(--text-muted))',
          boxShadow: status.ready ? '0 4px 14px hsl(var(--accent) / 0.28)' : 'none',
        }}
      >
        {status.publishing ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Rocket className="w-3.5 h-3.5" />
        )}
        <span>{status.publishing ? t('workspace.publishing') : t('publish.button')}</span>
      </button>
    );
  }

  // ─── Already published ──────────────────────────────────────────
  const publishedAgo = status.publishedAt
    ? formatDistanceToNowStrict(new Date(status.publishedAt), {
        locale: locale === 'en' ? enLocale : esLocale, addSuffix: true,
      })
    : '';

  return (
    <div
      className="hidden md:inline-flex items-center gap-2 rounded-full pl-2 pr-1 py-1 text-xs font-medium"
      style={{
        background: 'hsl(155 55% 12% / 0.55)',
        border: '1px solid hsl(155 60% 45% / 0.4)',
        boxShadow: '0 0 0 1px hsl(155 60% 45% / 0.08), 0 4px 20px -6px hsl(155 60% 30% / 0.35)',
      }}
      data-testid="publish-status-bar"
    >
      <span className="inline-flex items-center gap-1.5 pl-1.5" style={{ color: 'hsl(155 65% 60%)' }}>
        <span className="relative w-2 h-2">
          <span
            className="absolute inset-0 rounded-full animate-ping"
            style={{ background: 'hsl(155 70% 55%)', opacity: 0.4 }}
          />
          <span
            className="relative block w-2 h-2 rounded-full"
            style={{ background: 'hsl(155 75% 55%)' }}
          />
        </span>
        <span className="font-semibold tracking-wide uppercase text-[10px]">
          {locale === 'en' ? 'Live' : 'En vivo'}
        </span>
      </span>
      <span className="opacity-30">·</span>
      <span
        className="font-mono text-[11px]"
        style={{ color: 'hsl(155 40% 80%)' }}
        title={status.publishedAt || ''}
      >
        v{status.activeVersion}
      </span>
      {publishedAgo && !compact && (
        <>
          <span className="opacity-30">·</span>
          <span className="text-[10px] opacity-70" title={status.publishedAt || ''}>
            {publishedAgo}
          </span>
        </>
      )}
      <div className="flex items-center gap-0.5 ml-1 pl-2 border-l" style={{ borderColor: 'hsl(155 40% 30% / 0.3)' }}>
        <a
          href={status.publicUrl || '#'}
          target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-2 py-1 rounded-full hover:bg-white/[0.06] transition text-[11px]"
          style={{ color: 'hsl(155 55% 78%)' }}
          data-testid="publish-open-url"
          title={status.publicUrl || ''}
        >
          <ExternalLink className="w-3 h-3" />
          <span>{t('common.open')}</span>
        </a>
        <button
          type="button" onClick={copyPublicUrl}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-full hover:bg-white/[0.06] transition"
          data-testid="publish-copy-url"
          title={t('workspace.copyUrl')}
          style={{ color: 'hsl(155 50% 75%)' }}
        >
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
        </button>
        <button
          type="button"
          onClick={runPublish}
          disabled={status.publishing || !status.ready}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-full hover:bg-white/[0.06] transition disabled:opacity-40"
          data-testid="publish-republish"
          title={t('workspace.republish')}
          style={{ color: 'hsl(155 50% 75%)' }}
        >
          {status.publishing ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Radio className="w-3 h-3" />
          )}
          <span>{t('workspace.republish')}</span>
        </button>
        {onShowHistory && (
          <button
            type="button"
            onClick={onShowHistory}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full hover:bg-white/[0.06] transition"
            data-testid="publish-history-btn"
            title={t('workspace.history')}
            style={{ color: 'hsl(155 50% 75%)' }}
          >
            <History className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
};

export default PublishStatusBar;
